import { useBusinessEntity } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { AppType, useApp } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useFilingTabs } from '../../../hooks/useFilingTabs';
import { AddCustomFilingModal } from './AddCustomFilingModal';
import { FilingsTabs } from './FilingsTabs';

const Filings = ({
  businessEntityId,
  isAccountantAssignmentInProgress,
  incorporationCountry,
  loggedInUserEmail,
  loggedInUserId,
  includeIndirectTaxFilings = false,
  includeHiddenComplianceTypes = false,
  hideDefaultHeader = false,
  tabContentClassName,
  stickyTabs = false,
}: {
  businessEntityId: number;
  isAccountantAssignmentInProgress: boolean;
  incorporationCountry: Country;
  loggedInUserEmail: string;
  loggedInUserId: string;
  includeIndirectTaxFilings?: boolean;
  includeHiddenComplianceTypes?: boolean;
  /**
   * Hide the default "Filings" heading + Add Filing button.
   * Use when the consumer wants to render those externally
   * (e.g. aligned with a page header).
   */
  hideDefaultHeader?: boolean;
  /** Extra className applied to TabsContent inside FilingsTabs (e.g. "px-3xl"). */
  tabContentClassName?: string;
  /** Pin the tab bar to the top of the scroll container as the table scrolls. */
  stickyTabs?: boolean;
}) => {
  const { appType } = useApp();
  const addFilingModalVisibility = useToggle(false);

  const { refreshCompliances } = useFilingTabs({
    businessEntityId,
    includeIndirectTaxFilings,
    includeHiddenComplianceTypes,
  });

  // Modal needs companyId; derived from the entity since callers don't pass it.
  const { data: businessEntityForModal } = useBusinessEntity(businessEntityId);

  const handleOpenModal = () => {
    addFilingModalVisibility.show();
  };

  const handleCloseModal = () => {
    addFilingModalVisibility.hide();
  };

  return (
    <div className="flex flex-col gap-4xl">
      <div className="flex flex-col gap-3xl">
        {!hideDefaultHeader && (
          <div className="flex justify-between items-center">
            <h3 className="txt-extra-large-bold text-text-secondary">
              Filings
            </h3>
            {(isFeatureLaunched(FeatureLaunch.CUSTOM_FILINGS) ||
              appType === AppType.ADMIN) && (
              <Button variant="primary" onClick={handleOpenModal}>
                <Icons.Plus className="size-4" />
                <span>Add filing</span>
              </Button>
            )}
          </div>
        )}
        <FilingsTabs
          businessEntityId={businessEntityId}
          isAccountantAssignmentInProgress={isAccountantAssignmentInProgress}
          incorporationCountry={incorporationCountry}
          loggedInUserEmail={loggedInUserEmail}
          loggedInUserId={loggedInUserId}
          refreshCompliances={refreshCompliances}
          showThunderIcon={false}
          includeIndirectTaxFilings={includeIndirectTaxFilings}
          includeHiddenComplianceTypes={includeHiddenComplianceTypes}
          tabContentClassName={tabContentClassName}
          stickyTabs={stickyTabs}
        />
      </div>

      {!hideDefaultHeader && businessEntityForModal && (
        <AddCustomFilingModal
          isOpen={addFilingModalVisibility.visible}
          onClose={handleCloseModal}
          companyId={businessEntityForModal.generalCorporationInfo.companyId}
          businessEntityId={businessEntityId}
          refreshCompliances={refreshCompliances}
          loggedInUserId={loggedInUserId}
        />
      )}
    </div>
  );
};

export { Filings };
